import * as L from 'leaflet'
import URI from 'urijs'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import type {
  Address,
  AddressItem,
  ArrayToObjectFunction,
  Building,
  ComputeFunction,
  ConfigItem,
  EpcRegionData,
  EpcResult,
  Features,
  FormattedAddress,
  ObjectMapFunction,
  ReferenceData,
  Renovation,
  UserRoles,
} from '@/types/utils'

import { config } from '../assets/config/config.js'
import enums from './enums.js'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const utils = {} as any
utils.iso_regions = {
  'BE-VLG': 'Flanders',
  'BE-WAL': 'Wallonia',
  'BE-BRU': 'Brussels',
}

utils.cleanKeys = (
  obj: Record<string, unknown>,
  keyMapping?: Record<string, string>,
  functions?: Record<string, ComputeFunction>
): Record<string, unknown> => {
  const newObj = utils.objectMap(obj, (x: unknown) => x, keyMapping)

  if (functions)
    for (const [newKey, computeFunc] of Object.entries(functions)) {
      newObj[newKey] = computeFunc(newObj)
    }
  return newObj
}

utils.googleMapURL = (building: Building, pov?: { heading: number; pitch: number }): string =>
  `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${building.lat},${building.lng}${
    pov ? `&heading=${pov.heading}&pitch=${pov.pitch}` : ''
  }`
utils.areCloseEnough = (buildingX: Building, buildingY: Building, maxDist: number): boolean => {
  const locX = L.latLng([buildingX.lat, buildingX.lng])
  return locX.distanceTo([buildingY.lat, buildingY.lng]) <= maxDist
}

utils.renovation_types = (renovation_names: string[]): string[] => {
  // renovation_names: an array of renovation names
  // return an array of unique renovation types matching renovation_names
  const renovations = enums.allRenovations.filter((renovation: Renovation) =>
    renovation_names.includes(renovation.name)
  )
  return [...new Set(renovations.map((renovation: Renovation) => renovation.type))] as string[]
}

utils.getOptions = (key: string): unknown[] => {
  // TODO: Shouldn't we be using full path?
  return config.find((item: ConfigItem) => item.name === key)?.values || []
}

utils.clamp = (num: number, min: number, max: number): number => Math.min(Math.max(num, min), max)

utils.objectFromArray = function <T>(arr: T[], fn: ArrayToObjectFunction<T>): Record<string, unknown> {
  return arr.reduce((acc: Record<string, unknown>, b: T) => {
    const result = fn.call(null, b)
    if (typeof result === 'string') {
      acc[b as string] = result
    } else {
      const [k, v] = result
      if (v === undefined) {
        acc[b as string] = k
      } else {
        acc[k] = v
      }
    }
    return acc
  }, {})
}

utils.objectMap = <T, R>(
  obj: Record<string, T>,
  fn: ObjectMapFunction<T, R>,
  renameMap: Record<string, string> = {}
): Record<string, R> => {
  return Object.keys(obj).reduce((acc: Record<string, R>, k: string) => {
    const newKey = renameMap[k] || k
    acc[newKey] = fn.call(null, obj[k], k)
    return acc
  }, {})
}

utils.range = (start: number, end: number, step: number = 1): number[] => {
  return Array.from({ length: Math.floor((end - start) / step) + 1 }, (_, i) => start + i * step)
}

utils.convertRange = (value: number, r1: [number, number], r2: [number, number]): number => {
  return ((value - r1[0]) * (r2[1] - r2[0])) / (r1[1] - r1[0]) + r2[0]
}

utils.capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

utils.user_name = (user: string | { toString(): string }): string => {
  const name = user
  if (typeof name === 'string') {
    if (name.includes('@')) {
      // Extract name from email
      return name.split('@')[0].split('.').map(utils.capitalizeFirstLetter).join(' ')
    } else {
      return name
    }
  } else {
    return name.toString()
  }
}

utils.computeEpcLabelFromScore = (score: number | null | undefined, region: string): EpcResult => {
  if (typeof score !== 'number') {
    return {
      label: '?',
      color: utils.getEpcColorFromLabel(region, '?'),
    }
  }

  const [label] = Object.entries(enums.epcMapping[region]).find(
    ([_label, range]: [string, [number, number]]) => score > range[0] && score <= range[1]
  ) || ['', null]

  const color = utils.getEpcColorFromLabel(region, label)

  return {
    label,
    color,
  }
}

utils.computeEpcScoreFromLabel = (label: string, region: string): number => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const epcMapping = enums.epcMapping as any
  const epcRange = epcMapping[region][label]
  return isFinite(epcRange[0]) ? epcRange[0] + 1 : epcRange[1]
}

utils.epc_percent_mapping = (region: string, epcScore?: number): number | EpcRegionData => {
  const data: { [key: string]: EpcRegionData } = {
    [enums.iso_regions.brussels]: {
      epcs: [660, 580, 500, 420, 345, 275, 210, 150, 95, 45, 0],
      percents: [7, 5, 7, 13, 21, 19, 15, 9, 3, 1, 0],
    },
    [enums.iso_regions.flanders]: {
      epcs: [1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 0, -100],
      percents: [3, 2, 3, 5, 8, 13, 14, 18, 20, 13, 0, 0],
    },
    [enums.iso_regions.wallonia]: {
      epcs: [900, 800, 700, 600, 510, 425, 340, 255, 170, 86, 65, 0, -20],
      percents: [5, 3, 5, 8, 14, 17, 18, 15, 10, 4, 1, 0, 0],
    },
  }

  if (epcScore !== undefined) {
    const bestEpc = data[region].epcs[data[region].epcs.length - 1]
    if (epcScore < bestEpc) {
      return 0
    }
    let percent = 100
    for (let i = 0; i < data[region].epcs.length - 1; i++) {
      const currentEpcVal = data[region].epcs[i]
      if (epcScore <= currentEpcVal) {
        percent -= data[region].percents[utils.clamp(i + 1, 0, data[region].percents.length - 1)]
      }
    }
    return utils.clamp(percent, 0, 100)
  } else {
    return data[region]
  }
}

utils.getEpcColorFromLabel = (region: string, label: string): string => {
  if (!label || label === '?') return '#798DA6'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const epcColors = enums.epcColors as any
  return epcColors[region][label]
}

utils.quantile_formatter = (value: number): string => {
  if (value > 0.5) {
    return `top ${Math.ceil((1 - value) * 20) * 5}%`
  } else {
    return `bottom ${Math.ceil(value * 20) * 5}%`
  }
}

utils.isEmptyStr = (str: string): boolean => {
  return !str || !str.replace(/\s/g, '').length
}

/**
 * Joins a base URL with additional path segments and an optional query string.
 *
 * @param {string} baseUrl - The base URL to which paths will be appended.
 * @param {string|string[]} paths - A single path or an array of path segments to append to the base URL.
 * @param {string} [queryStr] - An optional query string in the format "key1=value1&key2=value2".
 *                              If provided, it will be appended to the URL with a '?' prefix.
 * @returns {string} - The fully constructed URL.
 * @throws {TypeError} - If the baseUrl is not provided or invalid.
 * @throws {Error} - If the constructed URL is invalid.
 */
utils.urlJoin = (baseUrl: string, paths: string | string[], queryStr: string = ''): string => {
  // TODO: prevent calls to urlJoin when baseUrl is not set
  if (!baseUrl) {
    // TODO: Figure out a way to catch and handle TypeError exceptions globally
    throw new TypeError('Invalid parameters for urlJoin function.')
  }

  let pathsArray: string[]
  // make paths an array:
  if (typeof paths === 'string') {
    pathsArray = [baseUrl, paths]
  } else {
    pathsArray = [baseUrl, ...paths.map((p) => p?.toString())]
  }

  // remove null values from paths (???)
  pathsArray = pathsArray.filter((path) => path !== null && path !== undefined)

  const hasTrailingSlash = pathsArray.length > 0 && pathsArray[pathsArray.length - 1].endsWith('/')

  // remove '/' from start and end of each path:
  pathsArray = pathsArray.map((path) => path.replace(/^\/|\/$/g, '')).filter((path) => path !== '')

  // join all path elements with '/'
  const fullPath =
    (baseUrl.startsWith('http') ? '' : '/') +
    pathsArray.join('/') +
    (hasTrailingSlash ? '/' : '') +
    (queryStr ? `?${queryStr}` : '')

  try {
    return new URI(fullPath).toString()
  } catch (e: unknown) {
    const error = e as Error
    throw new Error(`Invalid parameters for urlJoin function: ${error.message}`)
  }
}

// TODO: move/merge this to relevant composables
utils.getDocumentUrl = (
  requestRef: string,
  documentRef: string,
  queryStr: string = '',
  isFileUrl: boolean = true
): string => {
  const segments = [
    ReApi.VALUATION,
    ReApiVersion.CURRENT,
    ReEndpoint.REQUEST,
    requestRef,
    'document',
    documentRef,
  ]
  if (isFileUrl) {
    segments.push('file')
  }
  return queryKeyToUrl(segments).url + (queryStr ? `?${queryStr}` : '')
}

utils.getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift()
  }
  return undefined
}

utils.isoDatetime = (aDate?: Date): string =>
  (aDate ?? new Date()).toISOString().slice(0, 19).replace('T', ' ')

utils.parseFUDate = (dateStr: string | null | undefined): Date | null => {
  if (dateStr === null || dateStr === undefined) return null
  // If dateStr is in iso format, just pass it to the Date constructor
  if (dateStr.includes('T')) return new Date(dateStr)
  // "Weird, everything used to work, back in october, november and december…"
  const parts = dateStr.split(' ')
  const dateParts = parts[0].split('-')
  const timeParts = parts[1].split(':')

  const year = parseInt(dateParts[0], 10)
  const month = parseInt(dateParts[1], 10) - 1 // JavaScript counts months from 0
  const day = parseInt(dateParts[2], 10)
  const hour = parseInt(timeParts[0], 10)
  const minute = parseInt(timeParts[1], 10)
  const second = parseInt(timeParts[2], 10)

  return new Date(year, month, day, hour, minute, second)
}

utils.arrayToggle = <T>(array: T[], value: T): T[] => {
  const index = array.indexOf(value)

  if (index === -1) {
    return [...array, value]
  } else {
    return array.filter((el) => el !== value)
  }
}

utils.random_string = (length: number): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const values = new Uint32Array(length)
  window.crypto.getRandomValues(values)
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length]
  }
  return result
}

// https://stackoverflow.com/a/3464346 but modified because it made some mistakes or didn't conform to what we're trying to achieve
utils.dateDiffInBusinessDays = (dDate1: Date, dDate2: Date): number => {
  let iWeeks: number,
    iDateDiff: number,
    iAdjust = 0
  if (dDate2 < dDate1) return -1 // error code if dates transposed
  let iWeekday1 = dDate1.getDay() // day of week
  let iWeekday2 = dDate2.getDay()
  iWeekday1 = iWeekday1 == 0 ? 7 : iWeekday1 // change Sunday from 0 to 7
  iWeekday2 = iWeekday2 == 0 ? 7 : iWeekday2
  if (iWeekday1 > 5) iAdjust = 1
  iWeekday1 = iWeekday1 > 5 ? 5 : iWeekday1 // only count weekdays
  iWeekday2 = iWeekday2 > 5 ? 5 : iWeekday2

  // calculate differnece in weeks (1000mS * 60sec * 60min * 24hrs * 7 days = 604800000)
  // eslint-disable-next-line prefer-const
  iWeeks = Math.floor((dDate2.getTime() - dDate1.getTime()) / 604800000)

  if (iWeekday1 <= iWeekday2) {
    iDateDiff = iWeeks * 5 + (iWeekday2 - iWeekday1)
  } else {
    iDateDiff = (iWeeks + 1) * 5 - (iWeekday1 - iWeekday2)
  }

  iDateDiff -= iAdjust

  return iDateDiff
}

utils.formatAddress = (address?: Address): FormattedAddress => {
  if (!address)
    return {
      firstLine: '',
      secondLine: '',
    }
  const { streetname, streetnumber, postalcode, municipality, boxnumber } = address
  const box_appendix = boxnumber ? ` b ${boxnumber}` : ''
  return {
    firstLine: `${streetname} ${streetnumber}${box_appendix}`,
    secondLine: `${postalcode} ${municipality}`,
  }
}

utils.full_address = (address?: Address, features?: Features): string => {
  if (!address) return ''
  const building_type = features ? features.f_building_type : null
  if (utils.is_existing(building_type) || !building_type) {
    if (!address.streetname) return ''
    const box_appendix = address.boxnumber ? ` b ${address.boxnumber}` : ''
    if (address.postalcode !== null) {
      return `${address.streetname} ${address.streetnumber}${box_appendix}, ${address.postalcode} ${address.municipality}`
    }
  } else {
    // @ts-expect-error: Handle types
    const parcel_ids = features ? features.parcel_ids : null
    if (parcel_ids) {
      return parcel_ids[0]
    }
  }
  return ''
}

utils.via_address = (address?: Address, features?: Features): string => {
  if (!address) return ''
  const building_type = features ? features.f_building_type : null
  if (!utils.is_existing(building_type) && building_type !== null) {
    return utils.full_address(address)
  }
  return ''
}

utils.display_address = (item: AddressItem): string => {
  let address = utils.full_address(item.address, item.features)
  if (utils.via_address(item.address, item.features) !== '') {
    address = `${address} (via ${utils.via_address(item.address, item.features)})`
  }
  return address
}

utils.short_address = (address?: Address, features?: Features): string => {
  if (!address) return ''
  const building_type = features ? features.f_building_type : null
  if (utils.is_existing(building_type) || !building_type) {
    const box_appendix = address.boxnumber ? ` b ${address.boxnumber}` : ''
    if (address.postalcode) {
      return `${address.streetname} ${address.streetnumber}${box_appendix}, ${address.postalcode}`
    }
  } else {
    // @ts-expect-error: Handle types
    const parcel_ids = features ? features.parcel_ids : null
    if (parcel_ids) {
      return parcel_ids[0]
    }
  }
  return ''
}

//https://dev.to/gladchinda/javascript-tip-whatis-a-better-typeof-3g0o
utils.whatis = (value: unknown): string => {
  return Object.prototype.toString
    .call(value)
    .replace(/^\[object\s+([a-z]+)\]$/i, '$1')
    .toLowerCase()
}

utils.is_apartment = (f_building_type?: string): boolean =>
  f_building_type
    ? [enums.buildingTypes.APARTMENT, enums.buildingTypes.NEW_APARTMENT].includes(f_building_type)
    : false
utils.is_house = (f_building_type?: string): boolean =>
  f_building_type
    ? [enums.buildingTypes.HOUSE, enums.buildingTypes.NEW_HOUSE].includes(f_building_type)
    : false
utils.is_new = (f_building_type?: string): boolean =>
  f_building_type
    ? [enums.buildingTypes.NEW_HOUSE, enums.buildingTypes.NEW_APARTMENT].includes(f_building_type)
    : false
utils.is_plot = (f_building_type?: string): boolean =>
  f_building_type
    ? [enums.buildingTypes.CONSTRUCTION_PLOT, enums.buildingTypes.OTHER_PLOT].includes(f_building_type)
    : false
utils.is_garage = (f_building_type?: string): boolean =>
  f_building_type ? [enums.buildingTypes.GARAGE].includes(f_building_type) : false
utils.is_investment_property = (f_building_type?: string): boolean =>
  f_building_type ? [enums.buildingTypes.INVESTMENT].includes(f_building_type) : false
utils.is_commercial = (f_building_type?: string): boolean =>
  f_building_type ? [enums.buildingTypes.COMMERCIAL].includes(f_building_type) : false
utils.is_existing = (f_building_type?: string): boolean =>
  f_building_type ? !utils.is_new(f_building_type) && !utils.is_plot(f_building_type) : false

utils.forced_sale_value = (value: number): number => {
  // Linear interpolation of the forced sale factor based on reference values
  const references: ReferenceData = {
    X: [500000, 1500000],
    Y: [0.85, 0.7],
  }

  // Interval represents between which two reference values the value falls
  // eg. 2 values means 3 potential intervals
  let interval = 0
  while (value > references.X[interval] && interval < references.X.length) {
    interval++
  }

  let factor: number
  if (interval === 0) {
    // Value is lower than the lowest reference value
    factor = references.Y[0]
  } else if (interval === references.X.length) {
    // Value is higher than the highest reference value
    factor = references.Y[references.Y.length - 1]
  } else {
    // Coordinates of the two reference values between which the value falls
    const x1 = references.X[interval - 1]
    const x2 = references.X[interval]
    const y1 = references.Y[interval - 1]
    const y2 = references.Y[interval]

    // Linear interpolation
    factor = y1 + ((value - x1) * (y2 - y1)) / (x2 - x1)
  }

  return Math.round((value * factor) / 1000) * 1000
}

utils.rolesToList = (userRoles: UserRoles): string[] => {
  const rolesList: string[] = []
  for (const [mod, roles] of Object.entries(userRoles)) {
    for (const role of roles as string[]) {
      rolesList.push(`${mod}:${role}`)
    }
  }
  rolesList.sort()
  return rolesList
}

utils.listToRoles = (rolesList: string[]): UserRoles => {
  const userRoles: UserRoles = {}
  for (const userRole of rolesList) {
    const [mod, role] = userRole.split(':')
    if (mod in userRoles) {
      userRoles[mod].push(role)
    } else {
      userRoles[mod] = [role]
    }
  }

  return userRoles
}

utils.containsRole = (
  rolesObj: UserRoles,
  roleOrList: string | string[] | [string, string][],
  module?: string
): boolean => {
  // Can take a list of roles, or a list of [role, module] pairs

  if (typeof roleOrList !== 'string')
    return roleOrList.some((r: string | [string, string]) =>
      typeof r === 'string'
        ? utils.containsRole(rolesObj, r, module)
        : utils.containsRole(rolesObj, r[0], r[1])
    )

  const role = roleOrList

  if (!module) {
    for (const roles of Object.values(rolesObj)) {
      if ((roles as string[]).includes(role)) {
        return true
      }
    }
    return false
  }
  return rolesObj['*']?.includes(role) || rolesObj[module]?.includes(role)
}

// https://stackoverflow.com/a/67994693
utils.extractFilenameFromHeader = (disposition: string): string | null => {
  const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i
  const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i

  let fileName: string | null = null
  if (utf8FilenameRegex.test(disposition)) {
    fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)![1])
  } else {
    // prevent ReDos attacks by anchoring the ascii regex to string start and
    //  slicing off everything before 'filename='
    const filenameStart = disposition.toLowerCase().indexOf('filename=')
    if (filenameStart >= 0) {
      const partialDisposition = disposition.slice(filenameStart)
      const matches = asciiFilenameRegex.exec(partialDisposition)
      if (matches != null && matches[2]) {
        fileName = matches[2]
      }
    }
  }
  return fileName
}

utils.unique = <T>(arrOrObject: T[] | Record<string, T>, property?: string): Set<T> | Set<unknown> => {
  const arr = property
    ? (arrOrObject as Record<string, T>[])?.map((obj: Record<string, T>) => obj[property])
    : (arrOrObject as T[]) || []
  return new Set(arr)
}

export default utils
